/**
 * Visual Merchandising Task Manual PDF Generator
 * Generates stunning, professional PDF manuals for retail VM tasks from JSON
 * Using jsPDF + html2canvas for high-quality output
 */

import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import { generateHTML } from '@tiptap/react'
import * as DOMPurify from 'dompurify'
import { TaskPdfData, TaskPdfSubtask, TaskPdfContent } from './types'
import dayjs from 'dayjs'
import { richTextEditorExtensions } from 'components/CommonComponents/RichTextEditor'
import { orderTaskPdfItems, OrderedTaskPdfItem } from '../model/taskPdf'

/**
 * Resolve content to an HTML string.
 * Prefers rich_text (rendered via TipTap generateHTML) over legacy plain text.
 *
 * NOTE: <mark> is replaced with <span> because html2canvas misaligns the
 * background of <mark> relative to its text content (known rendering bug).
 */
const resolveHtml = (text?: string | null, rich_text?: any): string => {
  if (rich_text) {
    try {
      const html = generateHTML(rich_text, richTextEditorExtensions)
      // Allow style/data attributes so highlight background-color is not stripped
      const sanitized = DOMPurify.sanitize(html, {
        ADD_ATTR: ['style', 'data-color', 'data-background-color'],
      })
      // Replace <mark> → <span> (html2canvas misaligns <mark> backgrounds)
      const withSpans = sanitized.replace(/<mark([^>]*)>/g, '<span$1>').replace(/<\/mark>/g, '</span>')

      // html2canvas v1.4.1 has a bug: background-color on inline/inline-block elements renders
      // at the wrong vertical position relative to the text. Fix via DOMParser preprocessing
      // before the HTML string is placed in the DOM:
      try {
        const doc = new DOMParser().parseFromString(`<div id="r">${withSpans}</div>`, 'text/html')
        const root = doc.getElementById('r')
        if (root) {
          // Fix 1: Highlight background-color
          // html2canvas v1.4.1 bug: background-color on inline elements renders at the wrong
          // vertical position. Block elements are fine.
          //
          // Strategy A — walk UP through inline wrappers (<strong>, <em>, etc.) to find the
          // nearest block ancestor. If the span's text covers that entire block, move the
          // background to the block. Block backgrounds always render correctly in html2canvas.
          //
          // Strategy B — Partial / nested highlight (block has more content than just this span):
          // use a thick bottom border in the highlight color — borders always position correctly.
          const BLOCK_TAGS = new Set(['P', 'LI', 'BLOCKQUOTE', 'DIV', 'TD'])

          root.querySelectorAll<HTMLElement>('[style*="background-color"]').forEach((el) => {
            const bg = el.style.backgroundColor

            if (!bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)') return

            // Walk up through inline ancestors to find the nearest block container
            let ancestor: HTMLElement | null = el.parentElement

            while (ancestor && ancestor !== root && !BLOCK_TAGS.has(ancestor.tagName)) {
              ancestor = ancestor.parentElement
            }

            const coversEntireBlock =
              ancestor &&
              ancestor !== root &&
              BLOCK_TAGS.has(ancestor.tagName) &&
              (ancestor.textContent || '').trim() === (el.textContent || '').trim()

            if (coversEntireBlock) {
              // Strategy A: move background to the block ancestor
              el.style.removeProperty('background-color')
              ancestor!.style.setProperty('background-color', bg)
              if (ancestor!.tagName === 'P') {
                ancestor!.style.setProperty('border-radius', '4px')
                ancestor!.style.setProperty('padding', '2px 6px')
                ancestor!.style.setProperty('margin-bottom', '4px')
              }
            } else {
              // Strategy B: partial highlight — thick bottom border in highlight color
              el.style.removeProperty('background-color')
              el.style.setProperty('border-bottom', `8px solid ${bg}`)
              el.style.setProperty('padding-bottom', '1px')
            }
          })

          // Fix 2: TipTap wraps each list item's content in a <p> block element.
          // Make it inline so text sits on the same line as the list marker.
          root.querySelectorAll<HTMLElement>('li > p').forEach((p) => {
            p.style.setProperty('display', 'inline')
            p.style.setProperty('margin', '0')
            p.style.setProperty('padding', '0')
          })

          return root.innerHTML
        }
      } catch {
        // fall through to withSpans
      }
      return withSpans
    } catch {
      // fall through
    }
  }
  if (text?.trim()) return text.replace(/\n/g, '<br>')
  return ''
}

// ============================================================================
// TYPES
// ============================================================================

type OrderedItem = OrderedTaskPdfItem

export interface PdfTranslations {
  taskPeriod: string
  deadline: string
  task: string
  category: string
}

const DEFAULT_TRANSLATIONS: PdfTranslations = {
  taskPeriod: 'Task Period',
  deadline: 'Deadline',
  task: 'Task',
  category: 'Category',
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Convert timestamp string YYYYMMDDHHMMSS to readable format
 */
const timestampToDate = (tsStr: string): string => {
  try {
    const dt = dayjs(tsStr)
    return dt.format('MMMM DD, YYYY [at] hh:mm A')
  } catch {
    return tsStr
  }
}

/**
 * Resize a blob image to at most maxPx on its longest side and return as a base64 JPEG data URI.
 * Keeps embedded base64 strings small so the html2canvas output canvas stays within browser limits.
 */
const resizeImageBlob = (blob: Blob, maxPx = 1200): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(blob)

    img.onload = () => {
      URL.revokeObjectURL(objectUrl)

      const scale = Math.min(1, maxPx / Math.max(img.width, img.height))
      const w = Math.round(img.width * scale)
      const h = Math.round(img.height * scale)
      const cvs = document.createElement('canvas')

      cvs.width = w
      cvs.height = h
      cvs.getContext('2d')!.drawImage(img, 0, 0, w, h)
      resolve(cvs.toDataURL('image/jpeg', 0.85))
    }
    img.onerror = reject
    img.src = objectUrl
  })
}

/**
 * Download image from URL, resize it, and return as base64 data URI.
 * Uses a 10-second timeout per request to avoid hanging on slow URLs.
 */
const downloadImageAsBase64 = async (url: string): Promise<string | null> => {
  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 10_000)
    const response = await fetch(url, { signal: controller.signal })
    clearTimeout(timer)
    const blob = await response.blob()
    return await resizeImageBlob(blob)
  } catch (error) {
    console.warn(`Warning: Could not load image ${url}:`, error)
    return null
  }
}

/**
 * Load all images in parallel and convert to base64
 */
const loadAllImages = async (orderedItems: OrderedItem[]): Promise<void> => {
  console.log('Loading images...')

  // Collect all fetch tasks with their destination
  const tasks: Array<{ promise: Promise<string | null>; target: any; key: string; idx: number }> = []

  for (const item of orderedItems) {
    const itemData = item.data as any

    if (item.type === 'info_box') {
      const images: string[] = itemData.images || []
      images.forEach((url, idx) => {
        if (url) tasks.push({ promise: downloadImageAsBase64(url), target: itemData, key: 'images_base64', idx })
      })
    } else if (item.type === 'subtask') {
      const content = itemData.content || {}
      const images: string[] = content.images || []
      images.forEach((url, idx) => {
        if (url) tasks.push({ promise: downloadImageAsBase64(url), target: content, key: 'images_base64', idx })
      })
    }
  }

  const results = await Promise.allSettled(tasks.map((t) => t.promise))

  results.forEach((result, i) => {
    const { target, key, idx } = tasks[i]

    if (!target[key]) target[key] = []

    const value = result.status === 'fulfilled' ? result.value : null

    if (value) target[key][idx] = value
  })
}

// ============================================================================
// HTML GENERATORS
// ============================================================================

const generateInfoBoxHtml = (infoData: TaskPdfContent & { images_base64?: string[] }): string => {
  const textHtml = resolveHtml(infoData.text, infoData.rich_text)
  const images = infoData.images_base64 || []

  let imagesHtml = ''

  if (images.length > 0) {
    const totalImages = images.length
    imagesHtml = '<div class="images-container"><div class="images-grid">'
    images.forEach((imgData, idx) => {
      imagesHtml += `
        <div class="image-item">
          <img src="${imgData}" alt="">
          <div class="image-label">${idx + 1}/${totalImages}</div>
        </div>
      `
    })
    imagesHtml += '</div></div>'
  }

  return `
    <div class="info-box">
      <div class="info-box-text">${textHtml}</div>
      ${imagesHtml}
    </div>
  `
}

const generateSubtaskHtml = (
  subtaskData: TaskPdfSubtask & { content?: { text?: string; images_base64?: string[] } },
  number: number,
  translations: PdfTranslations
): string => {
  const title = subtaskData.title || 'Untitled Task'
  const category = subtaskData.category || ''
  const content = subtaskData.content || {}
  const textHtml = resolveHtml(content.text, content.rich_text)
  const images = content.images_base64 || []

  const categoryHtml = category ? `<div class="subtask-category">${translations.category}: ${category}</div>` : ''

  let imagesHtml = ''

  if (images.length > 0) {
    const totalImages = images.length
    imagesHtml = '<div class="images-container"><div class="images-grid">'
    images.forEach((imgData, idx) => {
      const isMain = idx === 0
      const labelClass = isMain ? 'image-label main-photo' : 'image-label'
      const star = isMain ? '<span class="star">★</span>' : ''

      imagesHtml += `
        <div class="image-item">
          <img src="${imgData}" alt="">
          <div class="${labelClass}">${star}${idx + 1}/${totalImages}</div>
        </div>
      `
    })
    imagesHtml += '</div></div>'
  }

  return `
    <div class="subtask">
      <div class="subtask-header">
        <div class="subtask-number">${translations.task} #${number}</div>
        <h2 class="subtask-title">${title}</h2>
        ${categoryHtml}
      </div>
      <div class="subtask-body">
        <div class="subtask-text">${textHtml}</div>
        ${imagesHtml}
      </div>
    </div>
  `
}

// ============================================================================
// SHARED CSS + SECTION WRAPPERS
// ============================================================================

/**
 * CSS shared across all per-section renders.
 * Note: cover-page uses fixed padding instead of min-height: 100vh so it
 * renders correctly in a detached div (no viewport context).
 */
const PAGE_CSS = `
  * { margin: 0; padding: 0; box-sizing: border-box; }

  /* Cover Page */
  .cover-page {
    background: linear-gradient(135deg, #f5e6d3 0%, #d4c5b9 100%);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 180px 60px;
    position: relative;
  }
  .cover-content { position: relative; z-index: 1; max-width: 800px; }
  .cover-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 58px; font-weight: 600; color: #3d3d3d;
    margin-bottom: 30px; line-height: 1.3; letter-spacing: -0.5px;
  }
  .cover-dates {
    background: rgba(255,255,255,0.7); padding: 30px 50px;
    border-radius: 12px; margin-top: 40px;
  }
  .cover-date-item { margin: 12px 0; font-size: 15px; }
  .cover-date-label {
    color: #8a7765; font-weight: 500; text-transform: uppercase;
    font-size: 11px; letter-spacing: 1.5px; margin-bottom: 5px;
  }
  .cover-date-value { color: #4a5568; font-weight: 600; font-size: 18px; }

  /* Section wrapper */
  .section-wrap {
    background: #faf9f7;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #4a5568; line-height: 1.7;
    padding: 40px 50px;
  }

  /* Info Box */
  .info-box {
    background: #fff8f0; border-left: 4px solid #d4a574;
    padding: 30px; margin: 0;
  }
  .info-box-text { color: #6b5d52; font-size: 15px; line-height: 1.8; }

  /* Rich text */
  .info-box-text p, .subtask-text p { margin: 0 0 8px 0; }
  .info-box-text p:last-child, .subtask-text p:last-child { margin-bottom: 0; }
  .info-box-text strong, .subtask-text strong { font-weight: 700; }
  .info-box-text em, .subtask-text em { font-style: italic; }
  .info-box-text h1, .subtask-text h1 { font-size: 26px; font-weight: 700; color: #3d3d3d; margin: 14px 0 8px; line-height: 1.3; }
  .info-box-text h2, .subtask-text h2 { font-size: 21px; font-weight: 700; color: #3d3d3d; margin: 12px 0 6px; line-height: 1.3; }
  .info-box-text h3, .subtask-text h3 { font-size: 17px; font-weight: 600; color: #3d3d3d; margin: 10px 0 4px; line-height: 1.3; }
  .info-box-text ul, .subtask-text ul { margin: 8px 0; padding-left: 20px; list-style-type: disc; list-style-position: inside; }
  .info-box-text ol, .subtask-text ol { margin: 8px 0; padding-left: 20px; list-style-type: decimal; list-style-position: inside; }
  .info-box-text li, .subtask-text li { margin: 4px 0; line-height: 1.7; }
  .info-box-text li > p, .subtask-text li > p { display: inline; margin: 0; }
  .info-box-text blockquote, .subtask-text blockquote { border-left: 3px solid #d4a574; padding-left: 14px; margin: 8px 0; color: #7a6e65; font-style: italic; }
  .info-box-text code, .subtask-text code { font-family: monospace; background: rgba(0,0,0,0.06); padding: 1px 5px; border-radius: 3px; font-size: 13px; }
  .info-box-text mark, .subtask-text mark { display: inline; border-radius: 2px; padding: 0 2px; }
  .info-box .images-container { margin: 30px -30px 0 -30px; }

  /* Subtask */
  .subtask { margin: 0; }
  .subtask-header {
    background: linear-gradient(135deg, #c9b5a0 0%, #a89985 100%);
    color: white; padding: 30px; margin-bottom: 0;
  }
  .subtask-number {
    display: inline-block; background: rgba(255,255,255,0.25);
    padding: 0px 18px 12px 18px; border-radius: 20px;
    font-size: 13px; font-weight: 600; margin-bottom: 12px; letter-spacing: 0.5px;
  }
  .subtask-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 32px; font-weight: 600; margin-bottom: 8px; line-height: 1.3;
  }
  .subtask-category { font-size: 14px; opacity: 0.95; font-weight: 500; font-style: italic; }
  .subtask-body { background: white; padding: 35px 30px; }
  .subtask-text { font-size: 15px; line-height: 1.8; color: #5a5a5a; margin-bottom: 25px; }

  /* Images */
  .images-container { margin: 30px -30px 0 -30px; }
  .images-grid { display: flex; flex-direction: column; gap: 20px; }
  .image-item { width: 100%; margin: 0; position: relative; }
  .image-item img { width: 100%; height: auto; display: block; }
  .image-label {
    position: absolute; top: 20px; right: 20px;
    background: rgba(138, 119, 101, 0.95); color: white;
    padding: 2px 20px 18px 26px; border-radius: 25px;
    font-size: 14px; font-weight: 600;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2); letter-spacing: 0.5px;
    border: 2px solid rgba(255,255,255,0.3);
  }
  .image-label.main-photo { background: linear-gradient(135deg, #d4a574 0%, #c9b5a0 100%); }
  .image-label .star { font-size: 16px; margin-right: 6px; }

  /* Footer */
  .page-footer {
    margin-top: 40px; padding-top: 20px;
    border-top: 1px solid #e8e4df;
    text-align: center; font-size: 11px; color: #a89985;
  }
`

/** Wrap inner HTML in a self-contained, styled div ready for html2canvas */
const wrapSection = (innerHtml: string): string => `
  <div style="width:794px;background:#faf9f7;font-family:Inter,-apple-system,sans-serif;color:#4a5568;line-height:1.7">
    <style>${PAGE_CSS}</style>
    ${innerHtml}
  </div>
`

const generateCoverSection = (
  title: string,
  startDate: string,
  endDate: string,
  translations: PdfTranslations
): string =>
  wrapSection(`
    <div class="cover-page">
      <div class="cover-content">
        <h1 class="cover-title">${title}</h1>
        <div class="cover-dates">
          <div class="cover-date-item">
            <div class="cover-date-label">${translations.taskPeriod}</div>
            <div class="cover-date-value">${startDate}</div>
          </div>
          <div class="cover-date-item">
            <div class="cover-date-label">${translations.deadline}</div>
            <div class="cover-date-value">${endDate}</div>
          </div>
        </div>
      </div>
    </div>
  `)

const generateItemSection = (item: OrderedItem, subtaskNumber: number, translations: PdfTranslations): string => {
  if (item.type === 'info_box') {
    return wrapSection(
      `<div class="section-wrap">${generateInfoBoxHtml(
        item.data as TaskPdfContent & { images_base64?: string[] }
      )}</div>`
    )
  }
  return wrapSection(
    `<div class="section-wrap">${generateSubtaskHtml(
      item.data as TaskPdfSubtask & { content?: { text?: string; images_base64?: string[] } },
      subtaskNumber,
      translations
    )}</div>`
  )
}

// ============================================================================
// SECTION RENDERER
// ============================================================================

const PAGE_W_MM = 210
const PAGE_H_MM = 297
// A4 page height in DOM pixels at RENDER_SCALE 1.0 (794px ≈ 210mm → 1123px ≈ 297mm)
const PAGE_H_DOM = Math.round(794 * (PAGE_H_MM / PAGE_W_MM))
const RENDER_SCALE = 1.0

/**
 * Render one HTML section to a canvas.
 * Uses position:absolute so the browser paints the full element (no viewport clipping).
 */
const renderSectionToCanvas = async (sectionHtml: string): Promise<HTMLCanvasElement> => {
  const wrapper = document.createElement('div')

  // position:absolute at (0,0) → browser renders full element height, no fixed-pos clipping
  wrapper.style.cssText = 'position:absolute;left:0;top:0;width:794px;z-index:-9999;pointer-events:none;'
  wrapper.innerHTML = sectionHtml
  document.body.appendChild(wrapper)

  // Wait for all images in this section to decode
  const imgs = Array.from(wrapper.querySelectorAll<HTMLImageElement>('img'))

  await Promise.all(
    imgs.map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            img.onload = () => resolve()
            img.onerror = () => resolve()
          })
    )
  )

  const canvas = await html2canvas(wrapper, {
    scale: RENDER_SCALE,
    useCORS: true,
    allowTaint: true,
    logging: false,
    backgroundColor: '#faf9f7',
  })

  document.body.removeChild(wrapper)
  return canvas
}

// ============================================================================
// MAIN EXPORT
// ============================================================================

/**
 * Generate a Visual Merchandising task manual PDF from JSON data
 */
export const generateTaskPdfBlobHtml = async (
  jsonData: TaskPdfData,
  translations?: Partial<PdfTranslations>
): Promise<Blob> => {
  console.log('Starting VM Task Manual PDF generation...')

  // Merge provided translations with defaults
  const mergedTranslations: PdfTranslations = {
    ...DEFAULT_TRANSLATIONS,
    ...translations,
  }

  // Extract data
  const fromTs = jsonData.from_ts || ''
  const deadline = jsonData.deadline || ''
  const title = jsonData.title || 'Visual Merchandising Task'
  const contents = jsonData.contents || []
  const subTasks = jsonData.sub_tasks || []

  // Convert timestamps
  const startDate = timestampToDate(fromTs)
  const endDate = timestampToDate(deadline)

  const orderedItems = orderTaskPdfItems(subTasks, contents)

  // Load all images and convert to base64
  await loadAllImages(orderedItems)

  console.log('Rendering PDF sections...')

  // Pre-load fonts once (shared across all section renders)
  if (document.fonts) {
    await document.fonts.ready
  }

  // Render each section to its own canvas, then create one PDF page per section
  // sized to that section's exact height. This avoids OOM from storing all canvases
  // at once by processing them sequentially.
  const canvasPageH = Math.round(PAGE_H_DOM * RENDER_SCALE)
  const canvases: HTMLCanvasElement[] = []

  // eslint-disable-next-line no-await-in-loop
  canvases.push(await renderSectionToCanvas(generateCoverSection(title, startDate, endDate, mergedTranslations)))

  let subtaskNumber = 0

  for (const item of orderedItems) {
    if (item.type === 'subtask') subtaskNumber++
    // eslint-disable-next-line no-await-in-loop
    canvases.push(await renderSectionToCanvas(generateItemSection(item, subtaskNumber, mergedTranslations)))
  }

  // Natural heights in mm based on canvas pixel height
  const naturalHsMM = canvases.map((canvas) => (canvas.height / canvasPageH) * PAGE_H_MM)
  // Page heights clamped to be taller than page width to prevent jsPDF from auto-swapping
  // width/height in portrait mode (jsPDF swaps when format[0] > format[1])
  const pageHsMM = naturalHsMM.map((h) => Math.max(h, PAGE_W_MM + 1))

  // Use pageHsMM for page creation (avoids swap bug), naturalHsMM for addImage (avoids distortion)
  const pdf = new jsPDF({ unit: 'mm', format: [PAGE_W_MM, pageHsMM[0]] })

  pdf.addImage(canvases[0].toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, PAGE_W_MM, naturalHsMM[0], undefined, 'FAST')

  for (let i = 1; i < canvases.length; i++) {
    pdf.addPage([PAGE_W_MM, pageHsMM[i]])
    pdf.addImage(canvases[i].toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, PAGE_W_MM, naturalHsMM[i], undefined, 'FAST')
  }

  const totalPages = pdf.getNumberOfPages()

  console.log('✓ VM Task Manual PDF generated successfully!')
  console.log(`  Title: ${title}`)
  console.log(`  Subtasks: ${subTasks.length}, Info boxes: ${contents.length}, Pages: ${totalPages}`)

  return pdf.output('blob')
}

/**
 * Download the PDF to user's device
 */
export const downloadTaskPdfHtml = async (
  data: TaskPdfData,
  filename?: string,
  translations?: Partial<PdfTranslations>
): Promise<void> => {
  const blob = await generateTaskPdfBlobHtml(data, translations)
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')

  link.href = url
  link.download = filename || `${data.title.replace(/[^a-zA-Z0-9]/g, '_')}_task.pdf`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}
